import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Snackbar, Stack, Table, TableBody, TableCell, TableHead,
    TableRow, Tooltip, Typography,
} from '@mui/material';
import {
    AI_LABELS, getAiEndUserBudgets, getAiEndUserUsage, getUserPremium, resetAiEndUserBudget,
} from '../Api';
import {
    AiEndUserBudgetUsageDto, AiEndUserUsageDto, AiUserBudgetWindowDto, AiUserBudgetWindowKind,
} from '../models/dtos/AiDto';
import UserPremiumAdminDto from '../models/dtos/UserPremiumAdminDto';
import UserSubscriptionsCard from './UserSubscriptionsCard';
import { ConfirmDialog } from './betupFeed/BetupFeedShared';
import { BudgetProgress } from './ai/AiHealthPanels';
import { formatLimitUsd, targetText } from './ai/AiUserBudgets';
import {
    AiActionError, AiErrorAlert, aiLoadErrorText, formatAiCount, formatAiDate, formatAiUsd, toAiActionError,
} from './ai/AiShared';

function windowLabel(w: AiUserBudgetWindowDto): string {
    return w.window === 'WEEK' ? 'Week' : `${w.hours} h window`;
}

function windowState(w: AiUserBudgetWindowDto): string {
    return w.blocked ? 'BLOCKING' : w.percent >= 80 ? 'WARNING' : 'OK';
}

const RESET_TEXT: Record<AiUserBudgetWindowKind, { title: string; label: string; text: string; done: string }> = {
    HOURS: {
        title: 'Reset the short window?',
        label: 'Reset short window',
        text: "The player's AI spend so far stops counting for the hours windows (e.g. 5 h) of every per-user budget, so a player blocked "
            + 'there can use Ace again right away. The week window keeps counting it. The reset is recorded with your name.',
        done: 'Short window reset.',
    },
    WEEK: {
        title: 'Reset the week?',
        label: 'Reset week',
        text: "The player's AI spend so far stops counting for the week and for the hours windows of every per-user budget — "
            + 'a fresh start for all their limits. The reset is recorded with your name.',
        done: 'Week (and short window) reset.',
    },
};

function BudgetsCard({ userId, budgets, loading, error, onReset }: {
    userId: number;
    budgets: AiEndUserBudgetUsageDto | null;
    loading: boolean;
    error: AiActionError | null;
    onReset: (window: AiUserBudgetWindowKind) => void;
}) {
    const hasHoursWindow = (budgets?.budgets ?? []).some(b => b.windows.some(w => w.window === 'HOURS'));
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack gap={1.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1} flexWrap="wrap">
                        <Box>
                            <Typography variant="h6">AI (Ace) per-user budgets</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Rolling caps on what the AI hub spends for this player, every caller's budgets.
                            </Typography>
                        </Box>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                            <Tooltip title={hasHoursWindow ? '' : 'No budget of this player has an hours window'}>
                                <span>
                                    <Button variant="outlined" color="warning" disabled={loading || !hasHoursWindow} onClick={() => onReset('HOURS')}>
                                        {RESET_TEXT.HOURS.label}
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button variant="outlined" color="error" disabled={loading} onClick={() => onReset('WEEK')}>
                                {RESET_TEXT.WEEK.label}
                            </Button>
                        </Stack>
                    </Stack>
                    <AiErrorAlert error={error} />
                    {loading && budgets == null && <CircularProgress size={24} />}
                    {budgets && (
                        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            {budgets.blocked
                                ? <Chip color="error" label="Blocked" />
                                : <Chip color="success" variant="outlined" label="Not blocked" />}
                            {budgets.primary ? (
                                <Typography variant="body2" color="text.secondary">
                                    Closest to its limit: {windowLabel(budgets.primary)}, {formatLimitUsd(budgets.primary.usedUsd)} of{' '}
                                    {formatLimitUsd(budgets.primary.limitUsd)} ({budgets.primary.percent.toFixed(1)}%)
                                    {budgets.primary.resetsAt ? `, resets ${formatAiDate(budgets.primary.resetsAt)}` : ''}
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="text.secondary">No window in use.</Typography>
                            )}
                        </Stack>
                    )}
                    {budgets && budgets.budgets.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                            No per-user budget applies to player #{userId}.
                        </Typography>
                    )}
                    {budgets?.budgets.map(b => (
                        <Box key={b.budgetId}>
                            <Stack direction="row" gap={1} alignItems="baseline" flexWrap="wrap">
                                <Typography variant="body2" fontWeight={600}>{b.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    tier {b.tier ?? 'Default'} · {b.caller ?? 'All callers'} · {targetText(b.agentCode, b.taskCode)}
                                </Typography>
                            </Stack>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Window</TableCell>
                                        <TableCell>Used</TableCell>
                                        <TableCell>Spent / limit</TableCell>
                                        <TableCell>State</TableCell>
                                        <TableCell>Resets at</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {b.windows.map(w => (
                                        <TableRow key={`${w.window}-${w.hours}`}>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{windowLabel(w)}</TableCell>
                                            <TableCell sx={{ minWidth: 160 }}><BudgetProgress percent={w.percent} state={windowState(w)} /></TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatLimitUsd(w.usedUsd)} / {formatLimitUsd(w.limitUsd)}</TableCell>
                                            <TableCell>
                                                {w.blocked
                                                    ? <Chip size="small" color="error" label="blocked" />
                                                    : <Chip size="small" variant="outlined" color="success" label="ok" />}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{w.resetsAt ? formatAiDate(w.resetsAt) : '—'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
}

function UsageCard({ usage, loading, error }: { usage: AiEndUserUsageDto | null; loading: boolean; error: string | null }) {
    const rows = (usage?.rows ?? []).slice().sort((a, b) => b.costUsd - a.costUsd);
    return (
        <Card variant="outlined">
            <CardContent>
                <Stack gap={1.5}>
                    <Box>
                        <Typography variant="h6">AI usage (last 30 days)</Typography>
                        {usage && (
                            <Typography variant="body2" color="text.secondary">
                                {usage.from} – {usage.to}: {formatAiCount(usage.calls)} calls, {formatAiUsd(usage.costUsd)}
                            </Typography>
                        )}
                    </Box>
                    {error && <Alert severity="error">{error}</Alert>}
                    {loading && usage == null && <CircularProgress size={24} />}
                    {usage && rows.length === 0 && (
                        <Typography variant="body2" color="text.secondary">No AI calls for this player in the period.</Typography>
                    )}
                    {rows.length > 0 && (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Agent / task</TableCell>
                                    <TableCell align="right">Calls</TableCell>
                                    <TableCell align="right">Errors</TableCell>
                                    <TableCell align="right">Blocked</TableCell>
                                    <TableCell align="right">Tokens in / out</TableCell>
                                    <TableCell align="right">Cache read</TableCell>
                                    <TableCell align="right">Cost</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r, i) => (
                                    <TableRow key={`${r.agentCode ?? ''}|${r.taskCode ?? ''}|${i}`}>
                                        <TableCell>{r.agentCode ? `agent ${r.agentCode}` : r.taskCode ? `task ${r.taskCode}` : '—'}</TableCell>
                                        <TableCell align="right">{formatAiCount(r.calls)}</TableCell>
                                        <TableCell align="right">{formatAiCount(r.errors)}</TableCell>
                                        <TableCell align="right">{formatAiCount(r.blocked)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatAiCount(r.inputTokens)} / {formatAiCount(r.outputTokens)}</TableCell>
                                        <TableCell align="right">{formatAiCount(r.cacheReadTokens)}</TableCell>
                                        <TableCell align="right">{formatAiUsd(r.costUsd)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

/** User page › Premium: Ace Premium (access, purchases), the player's AI (Ace) per-user budgets and AI usage. */
export default function UserPremiumTab({ userId }: { userId: number }) {
    const [budgets, setBudgets] = React.useState<AiEndUserBudgetUsageDto | null>(null);
    const [budgetsError, setBudgetsError] = React.useState<AiActionError | null>(null);
    const [budgetsLoading, setBudgetsLoading] = React.useState(true);
    const [usage, setUsage] = React.useState<AiEndUserUsageDto | null>(null);
    const [usageError, setUsageError] = React.useState<string | null>(null);
    const [usageLoading, setUsageLoading] = React.useState(true);
    const [pendingReset, setPendingReset] = React.useState<AiUserBudgetWindowKind | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [toast, setToast] = React.useState<string | null>(null);
    const [premium, setPremium] = React.useState<UserPremiumAdminDto | null>(null);
    const [premiumError, setPremiumError] = React.useState<string | null>(null);
    const [premiumLoading, setPremiumLoading] = React.useState(true);

    const load = React.useCallback(async () => {
        setBudgetsLoading(true);
        setUsageLoading(true);
        setPremiumLoading(true);
        const [budgetResult, usageResult, premiumResult] = await Promise.all([
            getAiEndUserBudgets(userId), getAiEndUserUsage(userId), getUserPremium(userId),
        ]);
        setPremiumLoading(false);
        setPremium(premiumResult);
        setPremiumError(premiumResult == null ? 'Failed to load subscriptions.' : null);
        setBudgetsLoading(false);
        setUsageLoading(false);
        setBudgets(budgetResult.data);
        setBudgetsError(toAiActionError(budgetResult, 'Failed to load per-user budgets'));
        setUsage(usageResult);
        setUsageError(usageResult == null ? aiLoadErrorText(AI_LABELS.endUserUsage, 'Failed to load AI usage.') : null);
    }, [userId]);

    React.useEffect(() => { load(); }, [load]);

    const runPremium = async (action: () => Promise<{ data: UserPremiumAdminDto | null; error: string | null }>, done: string) => {
        setBusy(true);
        const result = await action();
        setBusy(false);
        if (result.error != null) {
            setPremiumError(result.error);
            return false;
        }
        setPremiumError(null);
        if (result.data != null) setPremium(result.data);
        setToast(done);
        return true;
    };

    const runReset = async () => {
        if (pendingReset == null) return;
        const kind = pendingReset;
        setPendingReset(null);
        setBusy(true);
        const result = await resetAiEndUserBudget(userId, kind);
        setBusy(false);
        if (result.error != null) {
            setBudgetsError(toAiActionError(result, 'Reset failed'));
            return;
        }
        setBudgetsError(null);
        if (result.data != null) setBudgets(result.data);
        setToast(RESET_TEXT[kind].done);
    };

    return (
        <Stack gap={2} sx={{ py: 2 }}>
            <Stack direction="row" gap={1} alignItems="center">
                <Button variant="outlined" onClick={load} disabled={busy || budgetsLoading || usageLoading}>Refresh</Button>
                {busy && <CircularProgress size={22} />}
            </Stack>
            <UserSubscriptionsCard userId={userId} premium={premium} loading={premiumLoading || busy} error={premiumError}
                run={runPremium} />
            <BudgetsCard userId={userId} budgets={budgets} loading={budgetsLoading || busy} error={budgetsError}
                onReset={setPendingReset} />
            <UsageCard usage={usage} loading={usageLoading} error={usageError} />
            <ConfirmDialog
                open={pendingReset != null}
                title={pendingReset ? RESET_TEXT[pendingReset].title : ''}
                text={pendingReset ? RESET_TEXT[pendingReset].text : ''}
                confirmLabel={pendingReset ? RESET_TEXT[pendingReset].label : 'Reset'}
                confirmColor={pendingReset === 'WEEK' ? 'error' : 'warning'}
                onClose={() => setPendingReset(null)}
                onConfirm={runReset}
            />
            <Snackbar
                open={toast != null}
                autoHideDuration={3000}
                message={toast ?? ''}
                onClose={() => setToast(null)}
            />
        </Stack>
    );
}
